import React, { useRef, useEffect, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ContextualFormattingEnabledOptions } from '@atlaskit/editor-common/toolbar/types';
import { insm } from '@atlaskit/insm';
import Spinner from '@atlaskit/spinner/spinner';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- existing styles in this component use xcss from the legacy primitives package
import Box from '@atlaskit/primitives/box';
import { updatePageLoadInteractionName } from '@atlaskit/react-ufo/interaction-metrics';

import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { MovePageSuccessFlag } from '@confluence/page-tree/entry-points/MovePageSuccessFlag.tsx';
import { useFeatureCodesInURL } from '@confluence/feature-codes/entry-points/useFeatureCodesInURL.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { expVal } from '@confluence/feature-experiments';
import type { LoadingPriority } from '@confluence/loadable';
import { useGetCommentLinkType } from '@confluence/comments-util/entry-points/useGetCommentLinkType.ts';
import {
	CREATE_PAGE_EXPERIENCE,
	VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
	VIEW_PAGE_SESSION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { useExperienceState } from '@confluence/experience-tracker/entry-points/useExperienceState.ts';
import type { EditorAIExperienceMode } from '@confluence/editor-features/entry-points/EditorAIExperienceMode.ts';
import type { StreamingTelepointerIdentity } from '@confluence/editor-features/entry-points/StreamingTelepointerIdentity.ts';
import { FullPageEditorLoader } from '@confluence/full-page-editor/entry-points/FullPageEditorLoader.ts';
import { TreeRootForServerPlaceholderWrapper } from '@confluence/full-page-editor/entry-points/TreeRootForServerPlaceholderWrapper.ts';
import type { NativeCollabInjectedProps } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import { NativeCollabLoader } from '@confluence/native-collab/entry-points/NativeCollabLoader.ts';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent.ts';
import { useLivePageStoreActions } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { LivePageInviteNudge } from '@confluence/onboarding-editor/entry-points/components/LivePageInviteNudge.ts';
import { useLivePageOnboardingNudgeEligible } from '@confluence/onboarding-editor/entry-points/hooks/useLivePageOnboardingNudgeEligible.ts';
import { fg } from '@confluence/feature-gating';
import { NewCommentsProvider } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import { PageCommentsCountProvider } from '@confluence/comment-context/entry-points/PageCommentsCountContext.ts';
import {
	OnboardingNthUserContentTypeSpotlight,
	ContentType,
} from '@confluence/onboarding-nth-user-setup-modal/entry-points/OnboardingNthUserContentTypeSpotlight.tsx';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { getGlobalTransitionPreloader } from '@confluence/route-manager/entry-points/getGlobalTransitionPreloader.ts';
import { StartLivePageLoad } from '@confluence/live-pages-perf/entry-points/live-pages-perf.config.ts';
import { FullCycleRenderWrapper } from '@confluence/ssr-utilities/entry-points/FullCycleRender.ts';
import { RemoteLinkFlag } from '@confluence/experiment-jira-smart-links/entry-points/RemoteLinkFlagEntryPoint.ts';
import { useMeetingSignalReminder } from '@confluence/experiment-meeting-signal/entry-points/useMeetingSignalReminder.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

import { triggerViewedAnalytics } from './LivePageViewedAnalytics.ts';
import { usePermissionCheck } from './usePermissionCheck.tsx';
import { LivePageSessionExperienceStart } from './LivePageSessionExperienceStart.tsx';
import { LivePageShortcuts } from './LivePageShortcuts.tsx';
import { useEditorDataLoader } from './useEditorDataLoader.tsx';

export type LivePageComponentProps = {
	contentId: string;
	spaceKey: string | undefined;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	customPerfData?: Record<string, string | boolean | object>;
	loadingPriority?: LoadingPriority | null;

	// When LivePageComponent goes from not fast loaded -> fast loaded or vice-versa, ViewPageCommon remounts LivePageComponent (via renderLivePageEarly).
	// We need to inform LivePageContent if we're likely to remount, as that could cause NCS issues.
	isFastLoaded?: boolean;
	contextualFormattingMode?: ContextualFormattingEnabledOptions;
	disableEditorAI?: boolean;
	editorAIExperienceMode?: EditorAIExperienceMode;
	streamingTelepointerIdentity?: StreamingTelepointerIdentity;
	enableRemoteTextHighlight?: boolean;
	disablePrimaryToolbar?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
	hasAudioByline?: boolean;
	hasPageComments: boolean;
	hasInlineComments: boolean;
	hasCommentSessions?: boolean;
	onLoadingComplete?: (contentId: string) => void;
	onLoadingError?: (contentId: string, error: unknown) => void;
};
type LivePageContentProps = {
	canEdit: boolean;
	isSeamlessTransition: boolean;
	refetch: () => void;
	onNativeCollabLoad?: (contentId: string, viewMode: boolean | undefined) => void;
	onDraftSyncSuccess?: () => void; // Used to hook into the editor draft sync mutation
} & LivePageComponentProps;

const spinnerContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	height: '100vh',
});

// Handle aborting experiences
/* Why we're aborting each experience:
 * VIEW_PAGE_SESSION_EXPERIENCE: Aborted due to not wanting live pages to affect classic view-page-specific experience. Live pages have their own live-page-session experience.
 * VIEW_PAGE_EDIT_BUTTON_EXPERIENCE: Aborted due to editable live page starting but never finishing view-page/edit, because in ViewPageCommon, stickyHeader briefly mounts while loading `globalLiveState.isLive` and `editPermission`, which we unmount after we determine doc is an editable live page
 */
const useAbortNonLiveExperiences = () => {
	const experienceTracker = React.useContext(ExperienceTrackerContext);
	const { hasStopped: hasViewPageSessionExperienceStopped } =
		useExperienceState(VIEW_PAGE_SESSION_EXPERIENCE) || {};
	const { hasStopped: hasViewPageEditButtonExperienceStopped } =
		useExperienceState(VIEW_PAGE_EDIT_BUTTON_EXPERIENCE) || {};

	const nonCoreExperiencesToAbort = [
		{ name: VIEW_PAGE_SESSION_EXPERIENCE, hasStopped: hasViewPageSessionExperienceStopped },
		{
			name: VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
			hasStopped: hasViewPageEditButtonExperienceStopped,
		},
	];

	nonCoreExperiencesToAbort.forEach((abortedExperience) => {
		if (!abortedExperience.hasStopped) {
			experienceTracker.abort({
				name: abortedExperience.name,
				reason: 'Live Page Editor was opened',
			});
		}
	});
};

export const LivePageContent = ({
	contentId,
	spaceKey,
	canEdit,
	refetch,
	ClassicEditorContextProviderLoader,
	onNativeCollabLoad,
	customPerfData,
	onDraftSyncSuccess,
	loadingPriority,
	isSeamlessTransition,
	isFastLoaded,
	contextualFormattingMode,
	disableEditorAI,
	editorAIExperienceMode,
	streamingTelepointerIdentity,
	enableRemoteTextHighlight,
	disablePrimaryToolbar,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
	hasAudioByline,
	hasPageComments,
	hasInlineComments,
	hasCommentSessions,
}: LivePageContentProps): React.JSX.Element => {
	const { loomExternalOpenRoute } = useEmbeddedViewPageFeatures();

	useAbortNonLiveExperiences();

	const lastNativeCollabKeyRef = useRef(contentId);
	// NativeCollabComponent MUST remount if we're not a seamless transition, and it MUST NOT remount if we are a seamless transition.
	// Changing the key triggers a remount.
	// This means the key needs to stay the same if we go from non-seamless transition -> seamless transition
	// This fixes a bug where NativeCollabComponent unmounting on transition from non-seamless -> seamless would
	//   bug out the collab provider editor plugin, preventing NCS from connecting
	const nativeCollabKey = useMemo(() => {
		if (!isSeamlessTransition) {
			lastNativeCollabKeyRef.current = contentId;
			return contentId;
		}
		return lastNativeCollabKeyRef.current;
	}, [contentId, isSeamlessTransition]);

	const LivePageWrapper = ClassicEditorContextProviderLoader ?? React.Fragment;

	// If not seamless transition, ensure key is passed to NativeCollabLoader so it can clean up NCS provider on unmount when contentId changes
	return (
		<LivePageWrapper>
			{!isSeamlessTransition && <StartLivePageLoad contentId={contentId} viewMode={!canEdit} />}
			<ContentViewedEvent contentId={contentId} />
			<ExperienceSuccess name={CREATE_PAGE_EXPERIENCE} attributes={{ isLivePage: true }} />
			<LivePageSessionExperienceStart />
			<NativeCollabLoader
				contentId={contentId}
				spaceKey={spaceKey}
				contentType="page"
				key={nativeCollabKey}
				onPermissionChange={refetch}
				canEdit={canEdit}
			>
				{(propsFromNativeCollab: NativeCollabInjectedProps) => {
					const { viewMode } = propsFromNativeCollab;
					const livePageCustomPerfData = {
						...customPerfData,
						viewMode,
					};
					const livePageEditor = (
						<TreeRootForServerPlaceholderWrapper>
							<FullPageEditorLoader
								{...propsFromNativeCollab}
								extensionContextProvider={{
									ClassicEditorContextProvider: ClassicEditorContextProviderLoader,
								}}
								hasEditPermission={canEdit}
								customPerfData={livePageCustomPerfData}
								onDraftSyncSuccess={onDraftSyncSuccess}
								loadingPriority={loadingPriority}
								isFastLoadedLiveDoc={isFastLoaded}
								contextualFormattingMode={contextualFormattingMode}
								disableEditorAI={disableEditorAI}
								editorAIExperienceMode={editorAIExperienceMode}
								streamingTelepointerIdentity={streamingTelepointerIdentity}
								enableRemoteTextHighlight={enableRemoteTextHighlight}
								disablePrimaryToolbar={disablePrimaryToolbar}
								hasByLineContributors={hasByLineContributors}
								hasByLineExtensions={hasByLineExtensions}
								hasReactions={hasReactions}
								hasAudioByline={hasAudioByline}
								hasPageComments={hasPageComments}
								hasInlineComments={hasInlineComments}
								hasCommentSessions={hasCommentSessions}
								loomExternalOpenRoute={loomExternalOpenRoute}
							/>
							<LivePageShortcuts canEdit={canEdit} />
						</TreeRootForServerPlaceholderWrapper>
					);
					// TODO MODES-3871 Remove this triggerViewedAnalytics side effect when we cleanup LivePageComponent.
					// Trigger live page view analytics event once when the user views a live page.
					onNativeCollabLoad?.(contentId, viewMode);

					return (
						// This TreeRootForServerPlaceholderWrapper placed here to ensure the proper lookup of the placeholders for
						// SSRed loadables. Due to different component trees between server (LivePageComponent) and client (LivePageComponentServer)
						// the placeholder ids hierarchy is different. This wrapper terminates the lookup for placeholder ids on the common parent,
						// so it matches on both server and client.
						isSSRStreaming() ? (
							<SuspenseWithDataFetcher dataFetcherName="content-main">
								{livePageEditor}
							</SuspenseWithDataFetcher>
						) : (
							<FullCycleRenderWrapper>{livePageEditor}</FullCycleRenderWrapper>
						)
					);
				}}
			</NativeCollabLoader>
		</LivePageWrapper>
	);
};

const LivePageComponentInner = ({
	flags,
	contentId,
	spaceKey,
	ClassicEditorContextProviderLoader,
	customPerfData,
	loadingPriority,
	isFastLoaded,
	contextualFormattingMode,
	disableEditorAI,
	editorAIExperienceMode,
	streamingTelepointerIdentity,
	enableRemoteTextHighlight,
	disablePrimaryToolbar,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
	hasAudioByline,
	hasPageComments,
	hasInlineComments,
	hasCommentSessions,
	onLoadingComplete,
	onLoadingError,
}: LivePageComponentProps & WithFlagsProps): React.JSX.Element => {
	const {
		hasData: hasPermissionCheckData,
		canEdit,
		loading: isLoadingPermissionCheck,
		contentQueryError,
		refetch,
		networkStatus,
	} = usePermissionCheck(contentId);
	const prevCanEditRef = useRef<boolean | undefined>(undefined);
	const hasSentViewedAnalytics = useRef<boolean>(false);

	const { setMode } = useLivePageStoreActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isPreloading } = useEditorDataLoader({
		isLoadingPermissionCheck,
		contentId,
		spaceKey,
		canEdit,
	});
	const featureCodes = useFeatureCodesInURL();
	const commentLink = useGetCommentLinkType();

	const { showLivePageInviteFlag } = useLivePageOnboardingNudgeEligible({ contentId });
	// Live-page navigation can replace the content without remounting this component.
	// The content ID marks a new load so reminders set on the current page wait for the next one.
	const {
		consumeReminder: consumeMeetingSignalPageLoadReminder,
		isEnabled: isMeetingSignalReminderEnabled,
	} = useMeetingSignalReminder({
		consumptionBoundary: 'pageLoad',
		contentId,
		flags,
	});

	const transitionPreloaderCurrentCallbackIDs =
		getGlobalTransitionPreloader().getCurrentRouteCallbackIds();
	const isSeamlessTransition = fg('confluence_frontend_transition_preloading_system')
		? transitionPreloaderCurrentCallbackIDs.includes('live_doc_preloading_transition_callback_id')
		: false;

	useEffect(() => {
		return () => {
			// Reset the mode and analytics flag when contentId changes or component unmounts
			void setMode(undefined);
			prevCanEditRef.current = undefined;
			hasSentViewedAnalytics.current = false;
		};
	}, [contentId, setMode]);

	useEffect(() => {
		// Permissions on a page can change at anytime. Should update the mode if the new permissions are different from the previous ones.
		const hasPermissionsChanged = canEdit !== prevCanEditRef.current;
		if (hasPermissionsChanged) {
			void setMode(canEdit ? 'edit' : 'view');
		}
		prevCanEditRef.current = canEdit;
	}, [canEdit, contentId, setMode]);

	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const liveEditUFOName = isEmbeddedConfluence ? 'embedded-live-edit' : 'live-edit';

	const { overrideUFORouteName } = useRouteActions();
	useEffect(() => {
		overrideUFORouteName(liveEditUFOName);
		// overrideUFORouteName only updates the trace-based UFO system. For embedded
		// pages, we also need to update the legacy interaction-metrics system, which
		// was set to 'embedded-view-page' by EmbeddedViewPage.
		if (isEmbeddedConfluence) {
			updatePageLoadInteractionName(liveEditUFOName);
		}
		insm.overrideExperienceKey(liveEditUFOName);
		// Live doc transitions trigger a UFO route change but usually don't trigger a LivePageComponent remount, so we need to re-run on contentId change
	}, [overrideUFORouteName, contentId, liveEditUFOName, isEmbeddedConfluence]);

	const handleNativeCollabLoad = (contentId: string, viewMode: boolean | undefined) => {
		if (hasSentViewedAnalytics.current === false) {
			void triggerViewedAnalytics({
				contentId,
				editPermission: canEdit,
				createAnalyticsEvent,
				viewMode,
				...(featureCodes && featureCodes.from ? { from: featureCodes.from } : {}),
				...(commentLink && { commentLink }),
			});
			hasSentViewedAnalytics.current = true;
		}
	};

	const skipLoadingScreenWhenSSRed = window.__SSR_RENDERED__;
	const isShowingLoadingSpinner =
		!contentQueryError &&
		((isPreloading && !skipLoadingScreenWhenSSRed) ||
			// don't unmount live pages editor on permission refetch
			(isSSRStreaming()
				? !hasPermissionCheckData
				: isLoadingPermissionCheck && networkStatus !== NetworkStatus.refetch)) &&
		!isSeamlessTransition;

	useEffect(() => {
		if (isMeetingSignalReminderEnabled && !isShowingLoadingSpinner && !contentQueryError) {
			consumeMeetingSignalPageLoadReminder();
		}
	}, [
		consumeMeetingSignalPageLoadReminder,
		contentQueryError,
		isMeetingSignalReminderEnabled,
		isShowingLoadingSpinner,
	]);

	useEffect(() => {
		if (
			!contentQueryError &&
			!isShowingLoadingSpinner &&
			fg('confluence_ai_planner_bridge_route')
		) {
			onLoadingComplete?.(contentId);
		}
	}, [contentId, contentQueryError, isShowingLoadingSpinner, onLoadingComplete]);

	useEffect(() => {
		if (contentQueryError && fg('confluence_ai_planner_bridge_route')) {
			onLoadingError?.(contentId, contentQueryError);
		}
	}, [contentId, contentQueryError, onLoadingError]);

	if (contentQueryError) {
		return <ErrorDisplay error={contentQueryError} />;
	}

	if (isShowingLoadingSpinner) {
		return (
			<Box xcss={spinnerContainerStyles}>
				<Spinner interactionName="live-pages-loading-spinner" size="medium" />
			</Box>
		);
	}

	return (
		<NewCommentsProvider>
			<PageCommentsCountProvider contentId={contentId}>
				{expVal('confluence_modernized_move_dialog', 'isEnabled', false) && <MovePageSuccessFlag />}
				{fg('cc_fd_jira_issue_live_doc_prompt_switch') && <RemoteLinkFlag contentId={contentId} />}
				<LivePageContent
					contentId={contentId}
					spaceKey={spaceKey}
					ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
					canEdit={canEdit}
					refetch={refetch}
					onNativeCollabLoad={handleNativeCollabLoad}
					onDraftSyncSuccess={showLivePageInviteFlag}
					customPerfData={customPerfData}
					loadingPriority={loadingPriority}
					isSeamlessTransition={isSeamlessTransition}
					isFastLoaded={isFastLoaded}
					contextualFormattingMode={contextualFormattingMode}
					disableEditorAI={disableEditorAI}
					editorAIExperienceMode={editorAIExperienceMode}
					streamingTelepointerIdentity={streamingTelepointerIdentity}
					enableRemoteTextHighlight={enableRemoteTextHighlight}
					disablePrimaryToolbar={disablePrimaryToolbar}
					hasByLineContributors={hasByLineContributors}
					hasByLineExtensions={hasByLineExtensions}
					hasReactions={hasReactions}
					hasAudioByline={hasAudioByline}
					hasPageComments={hasPageComments}
					hasInlineComments={hasInlineComments}
					hasCommentSessions={hasCommentSessions}
				/>
				<OnboardingNthUserContentTypeSpotlight
					contentType={ContentType.LIVE_DOC}
					contentId={contentId}
				/>
				<LivePageInviteNudge />
			</PageCommentsCountProvider>
		</NewCommentsProvider>
	);
};

export const LivePageComponent: React.ComponentType<LivePageComponentProps> =
	withFlags(LivePageComponentInner);
